import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Course, Event, News, Room, School, TvSlide } from '@tanzspass/shared';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  retry,
  Subscription,
  switchMap,
  timer,
} from 'rxjs';
import { TsHttpService } from './ts-http.service';

export interface RssFeedItem {
  date: string;
  rights: string;
  title: string;
  imageUrl?: string;
}

/**
 * One room's schedule for the rest of today — mirrors the backend
 * RoomCourseBucket. Grouped per room so each panel slices a room's list on its
 * own; the immediate "next" course is `upcomingCourses[0]`.
 */
export interface RoomCourseBucket {
  room: Room;
  currentCourse: Course | null;
  upcomingCourses: Course[];
}

/** The aggregated payload the kiosk polls — mirrors the backend TvScreenPayloadDto. */
export interface TvScreenPayload {
  /** The kiosk's own room (drives layout); resolved server-side from the polled roomId. */
  room: Room;
  school: School;
  rss: RssFeedItem[];
  news: News[];
  roomCourses: RoomCourseBucket[];
  featuredCourses: Course[];
  events: Event[];
  tvSlides: TvSlide[];

  weather: any | null;
  hvv: { departures?: unknown[] } | null;
}

const TV_POLL_MS = 60_000;

/**
 * Single source of TV-screen data: one ~60s poll of GET /bff/tv/:schoolId,
 * shared by every panel via {@link payload$}. The backend serves each slice
 * from a cache with last-good fallback; here we keep the last good payload on
 * the client too (a failed/again-failed poll emits nothing, so panels keep
 * showing what they had). Replaces the former per-panel endpoints.
 */
@Injectable({ providedIn: 'root' })
export class TvScreenService {
  private readonly payload = new BehaviorSubject<TvScreenPayload | null>(null);
  readonly payload$: Observable<TvScreenPayload | null> = this.payload.asObservable();
  private sub?: Subscription;

  constructor(
    private http: TsHttpService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  /** Begin polling for a room. Idempotent — safe to call once the roomId is known. */
  start(roomId: number): void {
    if (this.sub || !roomId || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.sub = timer(0, TV_POLL_MS)
      .pipe(
        switchMap(() =>
          this.http.get<TvScreenPayload>(`bff/tv/room/${roomId}`).pipe(
            retry({ count: 2, delay: 5000 }),
            catchError(() => of(null)),
          ),
        ),
      )
      .subscribe((payload) => {
        if (payload) {
          this.payload.next(payload);
        }
      });
  }
}
